import type { CSSProperties } from "react"
import type { RendererProps } from "../core/element-registry"
import { ElementRenderer, liftV4Props } from "./element-renderer"
import {
  InputGroup,
  InputGroupButton,
  InputGroupInput,
  InputGroupText,
  InputGroupAddon,
} from "../components/ui/input-group"
import { cn } from "../lib/utils"
import { useRenderContext } from "../core/render-context"

export function InputGroupRenderer({ element }: RendererProps) {
  const { resolveSymbol } = useRenderContext()

  return (
    <InputGroup
      className={cn(element.className)}
      data-testid={`input-group-${element.id}`}
    >
      {element.children?.map((rawChild) => {
        // JITRULE Task 2: addon/button/input children may nest their fields
        // under `props`.
        const child = liftV4Props(rawChild)
        const childType = child.type

        if (childType === "button") {
          const label = child.text
            ? String(resolveSymbol(child.text) ?? "")
            : undefined
          return (
            <InputGroupButton
              key={child.id}
              className={cn(child.className)}
              data-cogo-element-id={child.id}
              data-cogo-type={child.type}
            >
              {label}
            </InputGroupButton>
          )
        }

        if (childType === "input") {
          const placeholder = child.placeholder
            ? String(resolveSymbol(child.placeholder) ?? "")
            : undefined
          const rawVal = (child as any).value ?? (child as any).defaultValue
          const defaultValue = rawVal !== undefined
            ? String(resolveSymbol(rawVal) ?? rawVal)
            : undefined
          return (
            <InputGroupInput
              key={child.id}
              placeholder={placeholder}
              defaultValue={defaultValue}
              readOnly={(child as any).readonly === true}
              className={cn(child.className)}
              data-cogo-element-id={child.id}
              data-cogo-type={child.type}
            />
          )
        }

        if (childType === "input-group-addon") {
          const align = (child as any).align as "inline-start" | "inline-end" | undefined
          const position = child.position && typeof child.position === "object"
            ? child.position as unknown as Record<string, unknown>
            : undefined
          const style = inputGroupAddonPositionStyle(position, child.style)
          return (
            <InputGroupAddon
              key={child.id}
              align={align}
              className={cn(child.className)}
              style={style}
              data-position={position ? JSON.stringify(position) : undefined}
              data-cogo-element-id={child.id}
              data-cogo-type={child.type}
            >
              {child.text ? String(resolveSymbol(child.text) ?? "") : null}
              {child.children?.map((gc) => (
                <ElementRenderer key={gc.id} element={gc} />
              ))}
            </InputGroupAddon>
          )
        }

        if (childType === "icon" || childType === "kbd") {
          // Icons and kbd render as InputGroupAddon (matching shadcn v4 pattern)
          const align = child.align as "inline-start" | "inline-end" | undefined
          return (
            <InputGroupAddon
              key={child.id}
              align={align}
              className={cn(child.className)}
              data-cogo-element-id={child.id}
              data-cogo-type={child.type}
            >
              <ElementRenderer element={child} />
            </InputGroupAddon>
          )
        }

        if (childType === "text") {
          return (
            <InputGroupText
              key={child.id}
              className={cn(child.className)}
              data-cogo-element-id={child.id}
              data-cogo-type={child.type}
            >
              <ElementRenderer element={child} />
            </InputGroupText>
          )
        }

        // Fallback: render as addon
        return (
          <InputGroupAddon
            key={child.id}
            className={cn(child.className)}
            data-cogo-element-id={child.id}
            data-cogo-type={child.type}
          >
            <ElementRenderer element={child} />
          </InputGroupAddon>
        )
      })}
    </InputGroup>
  )
}

function inputGroupAddonPositionStyle(
  position: Record<string, unknown> | undefined,
  authoredStyle: Record<string, string | number> | undefined,
): CSSProperties | undefined {
  const style: CSSProperties = {}
  const positionType = position?.type
  if (typeof positionType === "string"
    && ["relative", "absolute", "fixed", "sticky"].includes(positionType)) {
    style.position = positionType as CSSProperties["position"]
  }
  for (const key of ["top", "right", "bottom", "left"] as const) {
    const value = position?.[key]
    if (typeof value === "string" || typeof value === "number") {
      style[key] = value
    }
  }
  Object.assign(style, authoredStyle)
  return Object.keys(style).length > 0 ? style : undefined
}
